// src/components/PageShell.js
//
// Standard V18 subpage: a 260px "horizon" strip (distant eye, silhouette
// crosses, tentacle tips in WebGL) carrying seal + display title + quote,
// then a 1040px content column with an optional vertical side label.

import React from 'react';
import styled from 'styled-components';
import SceneCanvas from '../scene/SceneCanvas';
import { Seal } from './ui';
import { color, font, halftone } from '../theme/tokens';

const Page = styled.div`
    position: relative;
    min-height: 100%;
    background: ${color.void};
    color: ${color.text};
    font-family: ${font.body};
`;

const Strip = styled.header`
    position: relative;
    height: 260px;
    overflow: hidden;
    background: linear-gradient(180deg, ${color.void} 0%, #14040a 52%, #33070f 100%);
    &::after {
        content: '';
        position: absolute;
        left: 0; right: 0; bottom: 0;
        height: 1px;
        z-index: 3;
        background: linear-gradient(90deg, transparent, rgba(200, 50, 62, 0.7) 30%, rgba(200, 50, 62, 0.7) 70%, transparent);
    }
    @media (max-width: 720px) { height: 200px; }
`;

const Halftone = styled.div`
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    opacity: 0.32;
    ${halftone}
`;

const TitleBlock = styled.div`
    position: absolute;
    left: max(24px, calc((100% - 1040px) / 2));
    right: 24px;
    bottom: 36px;
    z-index: 4;
    display: flex;
    align-items: flex-end;
    gap: 22px;
    h1 {
        margin: 0;
        font-family: ${font.display};
        font-weight: 800;
        font-size: 60px;
        line-height: 0.9;
        letter-spacing: 0.16em;
        color: ${color.textBright};
        text-shadow: 0 0 24px rgba(200, 50, 62, 0.45);
        white-space: nowrap;
    }
    @media (max-width: 720px) {
        bottom: 22px;
        gap: 14px;
        h1 { font-size: 40px; letter-spacing: 0.14em; }
    }
`;

const TitleMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-bottom: 2px;
    min-width: 0;
    .en { font-family: ${font.mono}; font-size: 11px; letter-spacing: 0.5em; color: ${color.text3}; }
    .quote {
        font-family: ${font.display};
        font-size: 14px;
        letter-spacing: 0.3em;
        color: ${color.text2};
    }
    @media (max-width: 720px) {
        .quote { display: none; }
        .en { font-size: 10px; letter-spacing: 0.4em; }
    }
`;

const QuoteMobile = styled.p`
    display: none;
    margin: 20px 20px 0;
    font-family: ${font.display};
    font-size: 13px;
    letter-spacing: 0.2em;
    line-height: 1.8;
    color: ${color.text2};
    @media (max-width: 720px) { display: block; }
`;

const Body = styled.main`
    position: relative;
    box-sizing: border-box;
    width: 100%;
    max-width: ${({ $wide }) => ($wide ? 1240 : 1088)}px;
    margin: 0 auto;
    padding: 72px 24px 140px;
    @media (max-width: 720px) { padding: 28px 16px 120px; }
`;

const SideLabel = styled.div`
    position: absolute;
    left: -64px;
    top: 80px;
    writing-mode: vertical-rl;
    font-family: ${font.display};
    font-size: 13px;
    letter-spacing: 0.6em;
    color: ${color.text3};
    display: flex;
    align-items: center;
    gap: 14px;
    &::before { content: ''; display: block; width: 1px; height: 48px; background: ${color.blood}; }
    @media (max-width: 1260px) { display: none; }
`;

export default function PageShell({ num, title, en, quote, sideLabel, wide, children }) {
    return (
        <Page>
            <Strip>
                <SceneCanvas mode="strip" style={{ zIndex: 1 }} />
                <Halftone />
                <TitleBlock>
                    {num && <Seal $size={52}>{num}</Seal>}
                    <h1>{title}</h1>
                    <TitleMeta>
                        {en && <span className="en">{en}</span>}
                        {quote && <span className="quote">「{quote}」</span>}
                    </TitleMeta>
                </TitleBlock>
            </Strip>
            {quote && <QuoteMobile>「{quote}」</QuoteMobile>}
            <Body $wide={wide}>
                {sideLabel && <SideLabel>{sideLabel}</SideLabel>}
                {children}
            </Body>
        </Page>
    );
}
